import { useEffect, useState } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { LocationPhoneSelection, PersonPhoneSelection } from '@frontend/thread-header-context';
import { usePreferredOutboundNumberUserSettings } from '@frontend/use-preferred-outbound-number-user-settings';
import { usePopoverMenu } from '@frontend/design-system';
import { ActiveConversations } from './active-conversations';
import { LocationPhoneSelectionPopoverSubmenu } from './location-phone-selection-popover-submenu';
import { LocationsOptions } from './locations-options';
import { useBatchGetThreadStatusQuery } from './use-batch-get-thread-status-query';
import { useOutboundNumberSelectorView } from './use-outbound-number-selector-view';

type LocationPhoneSelectionPopoverMenuContentProps = {
  disableAutoThreadOpen?: boolean;
  selectedLocationPhone?: LocationPhoneSelection;
  selectedPersonPhone?: PersonPhoneSelection;
  groupParticipants?: string[];
  isGroup?: boolean;
  onSelectLocationPhone: (locationPhone: string, locationId: string, threadId?: string) => void;
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  activeIndex: number;
  hideCurrentLocationPhone?: boolean;
  isOpen: boolean;
};

/** A version of the `FromSelectionPopoverMenu` component with departments deprecated */
export const LocationPhoneSelectionPopoverMenuContents = ({
  disableAutoThreadOpen = false,
  selectedLocationPhone,
  selectedPersonPhone,
  groupParticipants,
  isGroup,
  onSelectLocationPhone,
  getItemProps,
  activeIndex,
  isOpen,
}: LocationPhoneSelectionPopoverMenuContentProps) => {
  const { t } = useTranslation('inbox');
  const { getScopeName, selectedLocationIds } = useAppScopeStore();
  const locationIdsWithACL = filterLocationIdsByACLs({
    locationIds: selectedLocationIds,
    acls: Permission.SMS_INBOX_MANAGE,
  });
  const isMultiLocation = locationIdsWithACL.length > 1;
  const {
    activeConversationCount,
    activeConversationsByLocation,
    allPhoneOptionsByLocation,
    availableConversationsNumbersByLocation,
    filteredLocationPhonesByLocation,
    hasActiveConversation,
  } = useBatchGetThreadStatusQuery({
    personPhone: selectedPersonPhone?.personPhone,
    groupParticipants,
    isGroup,
  });

  const { initializeView, setView, view } = useOutboundNumberSelectorView();
  useEffect(() => {
    // just make sure the view is initialized when opening the menu (there are multiple ways to close the menu)
    if (isOpen) {
      initializeView(disableAutoThreadOpen ? false : hasActiveConversation, isMultiLocation);
      setSelectedLocationId('');
    }
  }, [disableAutoThreadOpen, hasActiveConversation, initializeView, isMultiLocation, isOpen]);

  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const { onClick: openThread } = useMessageAction();

  const { getPreferredOutboundNumberForLocation, setPreferredOutboundNumberForLocation } =
    usePreferredOutboundNumberUserSettings();

  // active-conversations view = shows list of active conversations with the option to see all numbers
  if (view === 'active-conversations') {
    return (
      <ActiveConversations
        activeConversationCount={activeConversationCount}
        activeIndex={activeIndex}
        conversationsByLocation={activeConversationsByLocation}
        getItemProps={getItemProps}
        locationPhones={filteredLocationPhonesByLocation}
        onAllOutboundNumbersClick={() => setView(isMultiLocation ? 'all-locations' : 'all-numbers')}
        onSelectConversation={onSelectLocationPhone}
        selectedLocationPhone={selectedLocationPhone?.locationPhone}
      />
    );
  }

  // all-locations view = shows list of all location names (multi-location only)
  if (view === 'all-locations') {
    return (
      <LocationsOptions
        getItemProps={getItemProps}
        locationOptions={Object.keys(allPhoneOptionsByLocation || {}).sort((a, b) =>
          getScopeName(a).localeCompare(getScopeName(b))
        )}
        onBack={() => setView('active-conversations')}
        onSelectLocation={(locationId) => {
          setSelectedLocationId(locationId);
          if (disableAutoThreadOpen) {
            setView('all-numbers');
            return;
          }

          // if this comes from the new create thread flow (i.e. the user hasn't selected a location for the person phone yet), let this hook handle which location phone number to select
          if (!selectedLocationPhone?.locationPhone) {
            openThread({
              openInInbox: true,
              personPhone: selectedPersonPhone?.personPhone,
              threadGroupId: locationId,
            });
            return;
          }
          // when the user has already selected a location phone and they are going through this flow to update it, don't select a location phone automatically
          setView('all-numbers');
        }}
        selectedLocationPhone={selectedLocationPhone}
        showHeader={hasActiveConversation}
      />
    );
  }

  const appliedSelectedLocationId = (isMultiLocation ? selectedLocationId : locationIdsWithACL[0]) || '';
  const availableConversationNumbers = availableConversationsNumbersByLocation[appliedSelectedLocationId];
  const selectedLocationIdAvailableNumbers = (availableConversationNumbers || []).map((number) => number.locationPhone);
  const selectedLocationIdPhoneOptions = (filteredLocationPhonesByLocation[appliedSelectedLocationId] || []).filter(
    (lp) => !availableConversationNumbers?.length || selectedLocationIdAvailableNumbers.includes(lp.locationPhone)
  );
  const preferredOutboundNumberForLocation = getPreferredOutboundNumberForLocation(appliedSelectedLocationId);
  const defaultSmsOptionForLocation = selectedLocationIdPhoneOptions.find(
    (lp) => lp.defaultSms && lp.locationId === appliedSelectedLocationId
  );

  // set preferred outbound number for location to the location's default sms number if not set yet
  if (!preferredOutboundNumberForLocation && defaultSmsOptionForLocation) {
    setPreferredOutboundNumberForLocation(appliedSelectedLocationId, defaultSmsOptionForLocation.locationPhone);
  }

  // all-numbers view = show all numbers for a location name
  return (
    <LocationPhoneSelectionPopoverSubmenu
      getItemProps={getItemProps}
      onBack={() => {
        // go back to all-locations view if multi-location
        if (isMultiLocation) {
          setView('all-locations');
          setSelectedLocationId('');
          return;
        }

        // change back to active-conversations view if there are any active conversation
        if (hasActiveConversation) {
          setView('active-conversations');
          return;
        }
      }}
      onSelectLocationPhone={onSelectLocationPhone}
      phonesByLocation={allPhoneOptionsByLocation}
      phoneOptions={selectedLocationIdPhoneOptions} // should show ALL numbers for the selected location
      showHeader={isMultiLocation || hasActiveConversation}
      selectedLocationPhone={selectedLocationPhone?.locationPhone}
      title={isMultiLocation ? getScopeName(appliedSelectedLocationId) : t('All Outbound Numbers')}
    />
  );
};
